import { GAnalyticsEvent } from '@jaaluu/interfaces/GAnalyticEvent';
import { t } from '@shared/i18n';
import {
  CategoryType,
  CommunicationBasics,
  CourseService,
  Language,
  MainCategories
} from '@shared/openapi/jaaluu-api';
import sharedStores, { animate } from '@shared/shared-stores';
import { computed, ref, Ref } from 'vue';
import { courseProgress, gAnalytics, language, progress } from './';
import { SubCategory } from './category';

const FAVORITE_GROUPS_STORAGE_KEY = 'favoriteGroups';
const ACTIVE_GROUP_STORAGE_KEY = 'activeFavoriteGroup';
const LAST_CATEGORY_INDEX_KEY = 'lastCategoryIndex';
const SRS_INTERVALS = [1, 2, 5, 10, 25, 50, 100, 300];
const FIXED_FAV_GROUPS = ['default', 'course', 'phrases', 'sayings', 'vocabulary'];

type UrlType = 'feeds' | 'kids_lexicon' | 'videos' | 'wikipedia' | 'books';

export interface FavWord {
  word: string;
  langForeign: Language;
  langNative: Language;
  translations: string[];
  wordType: string;
  added_at: Date;
  storyIdx: number;
  last_seen: Date;
  imageName: string | null;
  last_level_change: Date;
  trained_success: number;
  trained_count: number;
  seen_count: number;
  srs_level: number;
  success_rate: number;
  context_sentence: string | null;
  groupName: string;
}

export interface FavUrl {
  url: string;
  title: string;
  type: UrlType;
  lang: Language;
  added_at: Date;
}

export interface ExampleSentence {
  id: string;
  sentence: string;
}

export interface WordmapData {
  wordmap: string;
  exampleSentences: ExampleSentence[];
  addedWords: WordmapData[];
}

export interface FavWordmap {
  word: string;
  data: WordmapData;
  lang: Language;
  added_at: Date;
}

export interface FavoriteGroup {
  words: FavWord[];
  courseWords: FavWord[];
  urls: FavUrl[];
  wordmaps: FavWordmap[];
  bookmarks: Record<string, number>;
  createdAt: Date;
  name: string;
}

export enum FavType {
  WORD = 'word',
  URL = 'url',
  WORDMAP = 'wordmap'
}

export default () => {
  const { langForeign, langNative } = language.state;
  const { addConfetti } = animate.funcs;
  const { notify } = sharedStores.snackbar.funcs;

  const favoriteGroups: Ref<Record<string, FavoriteGroup>> = ref({});
  const favoriteGroupsLangs: Ref<Record<string, Record<string, FavoriteGroup>>> = ref({});
  const categoryStates: Ref<any> = ref({});
  const activeGroupKey: Ref<string> = ref('default');
  const wordsToLearn: Ref<FavWord[]> = ref([]);
  const favWordsForReviewSrc: Ref<FavWord[]> = ref([]);
  const showAnimateWord = ref({
    word: '',
    show: false,
    newLevel: 0,
    oldLevel: 0
  });

  const showMilestoneAnimation = ref({
    show: false,
    score: 0
  });

  const openSynapsisScoreDialog = ref(false);

  const getFinishedSubcategoriesCount = (): number => {
    if (!langForeign.value || !langNative.value || !progress.state.progress.value) return 0;
    const langProgress = (progress.state.progress.value as any)[langForeign.value]?.[
      langNative.value
    ];
    if (!langProgress) return 0;
    let count = 0;
    for (const catType of Object.values(langProgress) as any[]) {
      for (const mainCat of Object.values(catType) as any[]) {
        for (const subCat of Object.values(mainCat) as any[]) {
          if (subCat.pct === 100) count++;
        }
      }
    }
    return count;
  };

  const getTotalSynapsisScore = (): number => {
    const wordsScore = Object.values(favoriteGroups.value).reduce((total, group) => {
      return total + group.words.reduce((sum, word) => sum + word.srs_level + 1, 0);
    }, 0);
    const categoryBonus = getFinishedSubcategoriesCount() * 50;
    const courseBonus =
      courseProgress.state.courseProgress.value.stories.filter((s) => s.completed).length * 50;
    return wordsScore + categoryBonus + courseBonus;
  };

  const getLangKey = () => {
    return `${langForeign.value}_${langNative.value}`;
  };

  // Computed properties for current active group
  const activeGroup = computed(() => {
    return favoriteGroups.value[activeGroupKey.value];
  });

  const favWords = computed(() => activeGroup.value?.words || []);
  const favCourseWords = computed(() => activeGroup.value?.courseWords || []);
  const favUrls = computed(() => activeGroup.value?.urls || []);
  const favWordmaps = computed(() => activeGroup.value?.wordmaps || []);
  const bookmarks = computed(() => activeGroup.value?.bookmarks || {});

  const favWordList = computed(() => {
    return favWords.value.map((f) => f.word);
  });
  const favUrlsList = computed(() => {
    return favUrls.value.map((f) => f.url);
  });
  const favWordmapsList = computed(() => {
    return favWordmaps.value.map((f) => f.word);
  });

  const favWordsLang = computed(() => {
    return favWords.value.filter((f) => f.langForeign === langForeign.value);
  });
  const favUrlsLang = computed(() => {
    return favUrls.value.filter((f) => f.lang === langForeign.value);
  });
  const favWordmapsLang = computed(() => {
    return favWordmaps.value.filter((f) => f.lang === langForeign.value);
  });

  // New computed properties to count all favorites across all groups
  const allFavWordsLang = computed(() => {
    const allWords: FavWord[] = [];
    Object.keys(favoriteGroups.value).forEach((groupName) => {
      allWords.push(...favoriteGroups.value[groupName].words);
    });
    return allWords.filter((f) => f.langForeign === langForeign.value);
  });

  const allFavUrlsLang = computed(() => {
    const allUrls: FavUrl[] = [];
    Object.keys(favoriteGroups.value).forEach((groupName) => {
      allUrls.push(...favoriteGroups.value[groupName].urls);
    });
    return allUrls.filter((f) => f.lang === langForeign.value);
  });

  const allFavWordmapsLang = computed(() => {
    const allWordmaps: FavWordmap[] = [];
    Object.keys(favoriteGroups.value).forEach((groupName) => {
      allWordmaps.push(...favoriteGroups.value[groupName].wordmaps);
    });
    return allWordmaps.filter((f) => f.lang === langForeign.value);
  });

  const createEmptyGroup = (name: string): FavoriteGroup => {
    return {
      words: [],
      courseWords: [],
      urls: [],
      wordmaps: [],
      bookmarks: {},
      createdAt: new Date(),
      name
    };
  };

  const setFavorites = () => {
    // Load favorite groups
    const groupsDataLangs = localStorage.getItem(FAVORITE_GROUPS_STORAGE_KEY);
    if (groupsDataLangs) {
      try {
        favoriteGroupsLangs.value = JSON.parse(groupsDataLangs);
        favoriteGroups.value = favoriteGroupsLangs.value[getLangKey()] || {};
      } catch (error) {
        console.error('Error loading favorite groups:', error);
      }
    }

    // Load active group key
    const activeKey = localStorage.getItem(ACTIVE_GROUP_STORAGE_KEY);
    if (activeKey) {
      activeGroupKey.value = activeKey;
    }

    // Ensure default group exists
    if (!favoriteGroups.value['default']) {
      favoriteGroups.value['default'] = createEmptyGroup('default');
    }
    if (!favoriteGroups.value['course']) {
      favoriteGroups.value['course'] = createEmptyGroup('course');
    }
    if (!favoriteGroups.value['phrases']) {
      favoriteGroups.value['phrases'] = createEmptyGroup('phrases');
    }
    if (!favoriteGroups.value['sayings']) {
      favoriteGroups.value['sayings'] = createEmptyGroup('sayings');
    }
    if (!favoriteGroups.value['vocabulary']) {
      favoriteGroups.value['vocabulary'] = createEmptyGroup('vocabulary');
    }
  };

  const saveFavoriteGroups = () => {
    favoriteGroupsLangs.value[getLangKey()] = favoriteGroups.value;
    localStorage.setItem(FAVORITE_GROUPS_STORAGE_KEY, JSON.stringify(favoriteGroupsLangs.value));
  };

  const saveActiveGroupKey = () => {
    localStorage.setItem(ACTIVE_GROUP_STORAGE_KEY, activeGroupKey.value);
  };

  const createFavoriteGroup = (groupName: string) => {
    if (favoriteGroups.value[groupName]) {
      notify({
        type: 'error',
        text: t('favorites.group_already_exists', { name: groupName }),
        icon: 'mdi-alert'
      });
      return false;
    }
    favoriteGroups.value[groupName] = createEmptyGroup(groupName);
    saveFavoriteGroups();
    notify({
      type: 'success',
      text: t('favorites.group_created', { name: groupName }),
      icon: 'mdi-folder-plus'
    });
    return true;
  };

  const deleteFavoriteGroup = (groupName: string) => {
    if (groupName === 'default') {
      notify({
        type: 'error',
        text: t('favorites.cannot_delete_default'),
        icon: 'mdi-alert'
      });
      return false;
    }
    if (!favoriteGroups.value[groupName]) {
      return false;
    }
    if (favoriteGroups.value[groupName].words.length > 0) {
      notify({
        type: 'warning',
        text: t('favorites.group_still_has_words'),
        icon: 'mdi-alert'
      });
      return;
    }
    delete favoriteGroups.value[groupName];
    if (activeGroupKey.value === groupName) {
      activeGroupKey.value = 'default';
      saveActiveGroupKey();
    }
    saveFavoriteGroups();
    notify({
      type: 'success',
      text: t('favorites.group_deleted', { name: groupName }),
      icon: 'mdi-folder-remove'
    });
    return true;
  };

  const switchFavoriteGroup = (groupName: string) => {
    if (!favoriteGroups.value[groupName]) {
      groupName = 'default';
    }
    activeGroupKey.value = groupName;
    saveActiveGroupKey();
    return true;
  };

  const renameFavoriteGroup = (oldName: string, newName: string) => {
    if (oldName === 'default') {
      notify({
        type: 'error',
        text: t('favorites.cannot_rename_default'),
        icon: 'mdi-alert'
      });
      return false;
    }
    if (!favoriteGroups.value[oldName]) {
      return false;
    }
    if (favoriteGroups.value[newName]) {
      notify({
        type: 'error',
        text: t('favorites.group_already_exists', { name: newName }),
        icon: 'mdi-alert'
      });
      return false;
    }
    const group = favoriteGroups.value[oldName];
    group.name = newName;
    favoriteGroups.value[newName] = group;
    delete favoriteGroups.value[oldName];
    if (activeGroupKey.value === oldName) {
      activeGroupKey.value = newName;
      saveActiveGroupKey();
    }
    saveFavoriteGroups();
    return true;
  };

  const toggleFavoriteWord = async (
    word: string,
    langForeign: Language,
    langNative: Language,
    wordType: string,
    translation: string,
    storyIdx: number = -1,
    remove = true,
    groupName: string | null = null,
    context_sentence: string | null = null
  ) => {
    if (wordType !== 'sentence') {
      word = word.toLocaleLowerCase();
      translation = translation.toLocaleLowerCase();
    }
    if (wordType.toLocaleLowerCase().includes('noun')) {
      if (langForeign === Language.DE) {
        word = word[0].toUpperCase() + word.slice(1).toLocaleLowerCase();
      }
      if (langNative === Language.DE) {
        translation = translation[0].toUpperCase() + translation.slice(1).toLocaleLowerCase();
      }
    }

    let imageName: string | null = null;
    if (storyIdx > -1 && context_sentence) {
      try {
        const res = await CourseService.getWordImageV1CourseWordImagePost({
          body: { word, context_sentence, language: langForeign }
        });
        imageName = res.data?.image ?? null;
      } catch {
        imageName = null;
      }
    }
    const targetGroupName = groupName ?? activeGroupKey.value;
    if (!favoriteGroups.value[targetGroupName]) {
      console.log('Group not found:', targetGroupName);
      notify({
        type: 'error',
        text: t('favorites.group_not_found', { name: targetGroupName }),
        icon: 'mdi-alert'
      });
      return;
    }

    const targetGroup = favoriteGroups.value[targetGroupName];
    const existingFavWord = targetGroup.words.find(
      (f) => f.word === word && f.wordType === wordType
    );

    const scoreBeforeToggle = getTotalSynapsisScore();

    if (existingFavWord && remove) {
      targetGroup.words = targetGroup.words.filter(
        (fW) => !(fW.word === word && fW.wordType === wordType)
      );
    } else if (!existingFavWord) {
      const translations = translation.split(',').map((t) => t.trim());
      const newFavWord = {
        word,
        langForeign,
        langNative,
        translations,
        wordType,
        storyIdx,
        imageName,
        added_at: new Date(),
        last_seen: new Date(),
        last_level_change: new Date(),
        trained_success: 0,
        trained_count: 0,
        seen_count: 1,
        srs_level: 0,
        success_rate: 0,
        context_sentence,
        groupName: targetGroupName
      };
      targetGroup.words.push(newFavWord);
      gAnalytics.funcs.sendEvent(GAnalyticsEvent.CLICK_FAVORITE_WORD, { word, wordType });
    } else {
      console.log('trans before:', existingFavWord.translations);
      const transSet = [
        ...new Set(existingFavWord.translations.concat(translation.split(',').map((t) => t.trim())))
      ];
      console.log('trans after', transSet);
      existingFavWord.translations = transSet;
    }
    const scoreAfterToggle = getTotalSynapsisScore();
    console.log(
      'Score before level up:',
      scoreBeforeToggle,
      'score after level up:',
      scoreAfterToggle
    );
    if (Math.floor(scoreBeforeToggle / 100) < Math.floor(scoreAfterToggle / 100)) {
      showMilestoneAnimation.value = { show: true, score: scoreAfterToggle };
      setTimeout(() => {
        showMilestoneAnimation.value = { show: false, score: 0 };
      }, 5000);
    }

    saveFavoriteGroups();
  };

  const checkTriggerMilestoneAnimation = (scoreBefore: number) => {
    const score = getTotalSynapsisScore();
    if (Math.floor(scoreBefore / 100) >= Math.floor(score / 100)) return;
    showMilestoneAnimation.value = { show: true, score };
    setTimeout(() => {
      showMilestoneAnimation.value = { show: false, score: 0 };
    }, 5000);
  };

  const toggleFavoriteUrl = (
    url: string,
    title: string | undefined | null,
    lang: Language,
    groupName: string | null = null
  ) => {
    const targetGroupName = groupName ?? activeGroupKey.value;
    if (!favoriteGroups.value[targetGroupName]) {
      notify({
        type: 'error',
        text: t('favorites.group_not_found', { name: targetGroupName }),
        icon: 'mdi-alert'
      });
      return;
    }

    const targetGroup = favoriteGroups.value[targetGroupName];
    const targetUrlsList = targetGroup.urls.map((f) => f.url);

    if (targetUrlsList.includes(url)) {
      targetGroup.urls = targetGroup.urls.filter((tv) => tv.url !== url);
    } else if (!title) {
      console.error('No title for url', url);
    } else {
      const type = url.split('/')[2] as UrlType;
      const newFav = {
        url,
        lang,
        type,
        title,
        added_at: new Date()
      };
      targetGroup.urls.push(newFav);
    }
    saveFavoriteGroups();
  };

  const toggleFavoriteWordmap = (
    word: string,
    data: WordmapData,
    lang: Language,
    groupName: string | null = null
  ) => {
    const targetGroupName = groupName ?? activeGroupKey.value;
    if (!favoriteGroups.value[targetGroupName]) {
      notify({
        type: 'error',
        text: t('favorites.group_not_found', { name: targetGroupName }),
        icon: 'mdi-alert'
      });
      return;
    }

    const targetGroup = favoriteGroups.value[targetGroupName];
    const targetWordmapsList = targetGroup.wordmaps.map((f) => f.word);

    if (targetWordmapsList.includes(word)) {
      targetGroup.wordmaps = targetGroup.wordmaps.filter((f) => f.word !== word);
    } else {
      const newFav = {
        word,
        data,
        lang,
        added_at: new Date()
      };
      targetGroup.wordmaps.push(newFav);
    }
    saveFavoriteGroups();
  };

  const addBookmark = (id: string, pageNumber: number) => {
    activeGroup.value.bookmarks[id] = pageNumber;
    saveFavoriteGroups();
  };

  const getDateToNextReview = (srsLevel: number, lastLevelChange: Date) => {
    const now = new Date();
    const srsIntervalInMins = getSrsIntervalInMintues(srsLevel);
    const minsSinceLastChange = (now.getTime() - new Date(lastLevelChange).getTime()) / 60000;
    const diffInM = Math.max(0, srsIntervalInMins - minsSinceLastChange);
    if (diffInM === 0) return now;
    return new Date(now.getTime() + diffInM * 60000);
  };

  const addTrainResult = (word: string, success: boolean, favGroup: string) => {
    let movedToNextLevel = false;
    let _favWords = favoriteGroups.value[favGroup].words;
    console.log('_favWords:', _favWords);
    const favWord = _favWords.find((f) => f.word.toLocaleLowerCase() === word.toLocaleLowerCase());
    console.log('Adding train result for', word, 'success:', success, favWord);

    if (!favWord) return movedToNextLevel;
    favWord.trained_count++;
    favWord.seen_count++;
    const now = new Date();
    const srsIntervalInMins = getSrsIntervalInMintues(favWord.srs_level);
    const minsSinceLastChange =
      (now.getTime() - new Date(favWord.last_level_change).getTime()) / 60000;
    if (success) {
      favWord.trained_success++;
    }
    favWord.success_rate = parseFloat(
      ((favWord.trained_success / favWord.trained_count) * 100).toFixed(0)
    );
    if (success) {
      if (
        favWord.srs_level < SRS_INTERVALS.length - 1 &&
        minsSinceLastChange >= srsIntervalInMins &&
        favWord.success_rate >= 50
      ) {
        movedToNextLevel = true;
        const oldLevel = favWord.srs_level;
        favWord.srs_level = Math.min(SRS_INTERVALS.length - 1, favWord.srs_level + 1);
        favWord.last_level_change = new Date();
        showAnimateWord.value = {
          word,
          show: true,
          oldLevel,
          newLevel: favWord.srs_level
        };
        /*  notify({
          type: 'success',
          text: t('favorites.success_level_up', { level: favWord.srs_level + 1 }),
          icon: 'mdi-weight-lifter'
        }); */
        setTimeout(() => {
          showAnimateWord.value = {
            word: '',
            show: false,
            oldLevel: 0,
            newLevel: 0
          };
        }, 3000);
      }
      addConfetti(false);
    }
    favWord.last_seen = new Date();
    saveFavoriteGroups();
    return movedToNextLevel;
  };

  const moveWordToSrsLevel = (word: string, wordType: string, groupName: string, level: number) => {
    const targetWord = favoriteGroups.value[groupName]?.words.find(
      (f) => f.word === word && f.wordType === wordType
    );
    if (!targetWord) return;
    targetWord.srs_level = level;
    targetWord.last_level_change = new Date();
    saveFavoriteGroups();
  };

  const getSuccessRateColor = (successRate: number) => {
    if (successRate < 50) return 'red';
    if (successRate < 80) return 'orange';
    return 'green';
  };

  const getSrsIntervalInMintues = (srsLevel: number) => {
    const intervalInDays = SRS_INTERVALS[srsLevel];
    const intervalInMins = intervalInDays * 60 * 24; // prod
    // const intervalInMins = intervalInDays * 60; // debug
    return intervalInMins;
  };

  const setFavWordsForReviewSrc = () => {
    const now = new Date();
    favWordsForReviewSrc.value = favWordsLang.value.filter(({ last_seen, srs_level }) => {
      const intervalInMins = getSrsIntervalInMintues(srs_level);
      const lastSeen = new Date(last_seen);
      const minsSinceLastSeen = (now.getTime() - lastSeen.getTime()) / 60000;
      return minsSinceLastSeen >= intervalInMins;
    });
  };

  const setLastCategoryIndex = (
    index: number,
    categoryType: CategoryType,
    mainCategory: MainCategories,
    subCategory: SubCategory
  ) => {
    if (!langForeign.value || !langNative.value) return;

    // Ensure nested objects exist on categoryStates
    if (!categoryStates.value[langForeign.value]) categoryStates.value[langForeign.value] = {};
    if (!categoryStates.value[langForeign.value][langNative.value])
      categoryStates.value[langForeign.value][langNative.value] = {};
    if (!categoryStates.value[langForeign.value][langNative.value][categoryType])
      categoryStates.value[langForeign.value][langNative.value][categoryType] = {};
    if (!categoryStates.value[langForeign.value][langNative.value][categoryType][mainCategory])
      categoryStates.value[langForeign.value][langNative.value][categoryType][mainCategory] = {};
    // Set value in-memory
    categoryStates.value[langForeign.value][langNative.value][categoryType][mainCategory][
      subCategory
    ] = index;

    categoryStates.value[langForeign.value][langNative.value][categoryType].lastMainCategory =
      mainCategory;
    categoryStates.value[langForeign.value][langNative.value][categoryType].lastSubCategory =
      subCategory;

    localStorage.setItem(LAST_CATEGORY_INDEX_KEY, JSON.stringify(categoryStates.value));
  };

  const getLastCategoryIndex = (
    categoryType: CategoryType,
    mainCategory: MainCategories,
    subCategory: SubCategory
  ) => {
    if (langForeign.value && langNative.value) {
      return categoryStates.value?.[langForeign.value]?.[langNative.value]?.[categoryType]?.[
        mainCategory
      ]?.[subCategory];
    }
  };

  const initLastCategoryState = () => {
    const lsCategoryState = localStorage.getItem(LAST_CATEGORY_INDEX_KEY);
    if (lsCategoryState) {
      return (categoryStates.value = JSON.parse(lsCategoryState));
    } else if (langForeign.value && langNative.value) {
      categoryStates.value[langForeign.value] = {};
      categoryStates.value[langForeign.value][langNative.value] = {
        phrases: {
          lastMainCategory: MainCategories.COMMUNICATION_BASICS,
          lastSubCategory: CommunicationBasics.GREETINGS
        }
      };
    }
  };

  return {
    state: {
      favoriteGroups,
      activeGroupKey,
      activeGroup,
      favWords,
      favWordsLang,
      wordsToLearn,
      favWordList,
      favCourseWords,
      favUrls,
      favUrlsLang,
      favUrlsList,
      favWordmaps,
      favWordmapsLang,
      favWordmapsList,
      bookmarks,
      allFavWordsLang,
      allFavUrlsLang,
      allFavWordmapsLang,
      categoryStates,
      showAnimateWord,
      showMilestoneAnimation,
      favWordsForReviewSrc,
      openSynapsisScoreDialog
    },
    funcs: {
      setFavorites,
      createFavoriteGroup,
      deleteFavoriteGroup,
      switchFavoriteGroup,
      renameFavoriteGroup,
      toggleFavoriteWord,
      toggleFavoriteUrl,
      toggleFavoriteWordmap,
      addBookmark,
      addTrainResult,
      setFavWordsForReviewSrc,
      getSuccessRateColor,
      getDateToNextReview,
      setLastCategoryIndex,
      getLastCategoryIndex,
      initLastCategoryState,
      moveWordToSrsLevel,
      getTotalSynapsisScore,
      checkTriggerMilestoneAnimation
    },
    consts: {
      SRS_INTERVALS,
      FIXED_FAV_GROUPS
    }
  };
};
